import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";
import { ShipmentDocumentCustomService } from "../../api/customServices/ShipmentDocumentCustomService";
import { documentPreviewKind } from "../../helpers/documentPreview";

interface ShipmentDocumentPreviewModalProps {
  shipmentId: string;
  docId: string;
  originalName: string;
  mimeType?: string | null;
  onClose: () => void;
  onDownload: () => void;
}

/**
 * In-browser preview for image and PDF documents.
 *
 * The file is private in Firebase Storage with no public URL, so it is fetched as a
 * blob through the authenticated download endpoint and rendered from an object URL.
 * The server's `Content-Disposition: attachment` only governs direct navigation and
 * does not affect a blob we hold ourselves.
 */
const ShipmentDocumentPreviewModal: React.FC<
  ShipmentDocumentPreviewModalProps
> = ({ shipmentId, docId, originalName, mimeType, onClose, onDownload }) => {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const kind = documentPreviewKind(originalName, mimeType);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    ShipmentDocumentCustomService.fetchBlob(shipmentId, docId)
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      // Object URLs pin the blob in memory until revoked.
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [shipmentId, docId]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={originalName}
      className="shipment-document-preview-modal"
    >
      <div className="shipment-document-preview">
        {error ? (
          <p className="shipment-document-preview__error">
            {t("shipment_document_preview_failed")}
          </p>
        ) : !blobUrl ? (
          <p className="shipment-document-preview__loading">
            {t("loading_text")}
          </p>
        ) : kind === "image" ? (
          <img
            src={blobUrl}
            alt={originalName}
            className="shipment-document-preview__image"
          />
        ) : (
          <iframe
            src={blobUrl}
            title={originalName}
            className="shipment-document-preview__frame"
          />
        )}
      </div>
      <div className="shipment-document-preview__actions">
        <button className="btn btn-secondary" onClick={onClose}>
          {t("shipment_document_preview_close")}
        </button>
        <button className="btn btn-primary" onClick={onDownload}>
          {t("shipment_document_download")}
        </button>
      </div>
    </Modal>
  );
};

export default ShipmentDocumentPreviewModal;
